/* Product baselines (designs/product-baselines.md): what a product's team agreed, which
   version, and who approved it.

   A TEMPLATE is data (BL1–BL6): roles, typed columns, an optional grouping
   column that drives each row's RACI, and ordered approval stages. A
   BASELINE is written once, belongs to one product (BL25), and holds append-only VERSIONS drafted on one
   template revision (BL7). Approval is an append-only EVENT log per version
   — submit, approve, reject — and the state is derived from it here and
   nowhere else (BL10–BL13). */

import type { Stamp, UserRef } from "./organizer-roles";
import { namesFinancialTerm, FINANCIAL_REFUSAL } from "./feeds";

/* ---- templates (BL1–BL6) ---- */

export const COLUMN_TYPES = ["text", "longtext", "number", "date", "choice", "person"] as const;
export type ColumnType = (typeof COLUMN_TYPES)[number];
export const COLUMN_TYPE_LABEL: Record<ColumnType, string> = {
  text: "Text",
  longtext: "Long text",
  number: "Number",
  date: "Date",
  choice: "Pick-list",
  person: "Person",
};

export const STAGE_RULES = ["all", "any"] as const;
export type StageRule = (typeof STAGE_RULES)[number];
export const STAGE_RULE_LABEL: Record<StageRule, string> = { all: "Everyone approves", any: "Any one approves" };

export const BASELINE_LIMITS = {
  name: 80,
  label: 80,
  key: 40,
  description: 2000,
  roles: 10,
  columns: 30,
  options: 30,
  stages: 10,
  title: 120,
  summary: 4000,
  changeNote: 2000,
  comment: 2000,
  url: 500,
  rows: 500,
  text: 200,
  longtext: 4000,
  note: 1000,
} as const;

export interface TemplateRole {
  key: string;
  label: string;
}

export interface Column {
  key: string;
  label: string;
  type: ColumnType;
  required: boolean;
  /* the cell may carry the orange Unconfirmed flag (BL3) */
  estimateAllowed: boolean;
  /* a choice column's options; empty for every other type */
  options: string[];
}

export interface Stage {
  key: string;
  name: string;
  roleKeys: string[];
  rule: StageRule;
}

/* BL4 — one choice column; each option maps to the accountable role */
export interface Grouping {
  columnKey: string;
  accountable: Record<string, string>;
}

export interface TemplateBody {
  name: string;
  description: string;
  roles: TemplateRole[];
  ownerRole: string;
  columns: Column[];
  grouping: Grouping | null;
  stages: Stage[];
}

export interface TemplateRevision extends TemplateBody {
  templateId: string;
  revision: number;
  recorded: Stamp;
}

/* ---- baselines (BL7, BL8) ---- */

export type CellValue = string | number | UserRef | null;
export interface Cell {
  value: CellValue;
  unconfirmed: boolean;
}

export interface Row {
  id: string;
  cells: Record<string, Cell>;
  /* a review finding on the row */
  note: string | null;
}

export interface BaselineContent {
  templateRevision: number;
  /* every template role's key → the person holding it, null = not recorded */
  roleHolders: Record<string, UserRef | null>;
  summary: string;
  link: string | null;
  rows: Row[];
}

export interface Baseline {
  id: string;
  productId: string;
  templateId: string;
  title: string;
  recorded: Stamp;
}

export interface BaselineVersion {
  baselineId: string;
  version: number;
  content: BaselineContent;
  changeNote: string;
  recorded: Stamp;
}

/* ---- approval (BL10–BL13) ---- */

export const EVENT_ACTIONS = ["submit", "approve", "reject"] as const;
export type EventAction = (typeof EVENT_ACTIONS)[number];

/* `recorded.by` is the person who acted */
export interface BaselineEvent {
  baselineId: string;
  version: number;
  seq: number;
  action: EventAction;
  /* the stage approved or rejected in; null for a submit */
  stageKey: string | null;
  roleKey: string;
  comment: string | null;
  recorded: Stamp;
}

export type ApprovalState = "draft" | "in-approval" | "agreed" | "rejected";
export const APPROVAL_STATE_LABEL: Record<ApprovalState, string> = {
  draft: "Draft",
  "in-approval": "In approval",
  agreed: "Agreed",
  rejected: "Rejected",
};

export interface Status {
  state: ApprovalState;
  /* the stage awaiting approval, while in approval */
  stage: Stage | null;
  /* role keys that approved the current stage so far */
  approved: string[];
  /* a newer version exists; a draft or in-approval version is then closed */
  superseded: boolean;
}

export interface VersionView extends BaselineVersion {
  status: Status;
  events: BaselineEvent[];
}

export interface BaselineDetail {
  baseline: Baseline;
  /* oldest first; the last is current — the one being drafted or approved */
  versions: VersionView[];
  /* BL22 — the latest Agreed version, which stays in force while a newer
     version is drafted, in approval, or rejected; null = never agreed */
  inForce: number | null;
  /* every template revision a version was drafted on, plus the latest */
  templates: TemplateRevision[];
}

export interface BaselineView {
  id: string;
  productId: string;
  templateId: string;
  templateName: string;
  title: string;
  /* the current version and its state */
  version: number;
  state: ApprovalState;
  stageName: string | null;
  /* the version in force (BL22); equal to `version` when nothing newer is proposed */
  inForce: number | null;
  updated: Stamp;
}

export interface NewBaseline {
  productId: string;
  templateId: string;
  title: string;
  content: BaselineContent;
  changeNote: string;
}

/* ---- deriving state (BL12) ---- */

/* Replay one version's events over its template's stages. Events were
   checked when written (actionReason), so the replay trusts their order;
   an event that does not fit is ignored rather than guessed at. */
export function statusOf(template: TemplateBody, events: readonly BaselineEvent[], superseded: boolean): Status {
  let state: ApprovalState = "draft";
  let index = -1;
  let approved: string[] = [];
  for (const e of [...events].sort((a, b) => a.seq - b.seq)) {
    if (e.action === "submit" && state === "draft") {
      state = "in-approval";
      index = 0;
      approved = [];
    } else if (state === "in-approval") {
      const stage = template.stages[index];
      if (!stage || e.stageKey !== stage.key || !stage.roleKeys.includes(e.roleKey)) continue;
      if (e.action === "reject") {
        state = "rejected";
        continue;
      }
      if (!approved.includes(e.roleKey)) approved = [...approved, e.roleKey];
      if (stage.rule === "any" || stage.roleKeys.every((k) => approved.includes(k))) {
        index += 1;
        approved = [];
        if (index >= template.stages.length) state = "agreed";
      }
    }
  }
  return {
    state,
    stage: state === "in-approval" ? (template.stages[index] ?? null) : null,
    approved,
    superseded,
  };
}

export function templateAt(templates: readonly TemplateRevision[], templateId: string, revision: number): TemplateRevision | null {
  return templates.find((t) => t.templateId === templateId && t.revision === revision) ?? null;
}

export function latestTemplate(templates: readonly TemplateRevision[], templateId: string): TemplateRevision | null {
  return templates.filter((t) => t.templateId === templateId).reduce<TemplateRevision | null>(
    (best, t) => (best === null || t.revision > best.revision ? t : best),
    null,
  );
}

export function detailFrom(
  baseline: Baseline,
  versions: readonly BaselineVersion[],
  events: readonly BaselineEvent[],
  templates: readonly TemplateRevision[],
): BaselineDetail {
  const last = versions.length;
  const own = templates.filter((t) => t.templateId === baseline.templateId);
  const views = versions.map((v): VersionView => {
    const mine = events.filter((e) => e.version === v.version).sort((a, b) => a.seq - b.seq);
    const template = templateAt(own, baseline.templateId, v.content.templateRevision);
    const status: Status = template
      ? statusOf(template, mine, v.version !== last)
      : { state: "draft", stage: null, approved: [], superseded: v.version !== last };
    return { ...v, events: mine, status };
  });
  /* BL22: a proposed change never unseats what is agreed — the latest
     Agreed version is in force, and is not superseded, until a newer
     version is itself agreed */
  const inForce = views.filter((v) => v.status.state === "agreed").at(-1)?.version ?? null;
  return {
    baseline,
    templates: own,
    inForce,
    versions: views.map((v) => (v.version === inForce ? { ...v, status: { ...v.status, superseded: false } } : v)),
  };
}

export function viewFrom(detail: BaselineDetail): BaselineView {
  const current = detail.versions.at(-1)!;
  const { baseline } = detail;
  return {
    id: baseline.id,
    productId: baseline.productId,
    templateId: baseline.templateId,
    templateName: latestTemplate(detail.templates, baseline.templateId)?.name ?? baseline.templateId,
    title: baseline.title,
    version: current.version,
    state: current.status.state,
    stageName: current.status.stage?.name ?? null,
    inForce: detail.inForce,
    updated: current.events.at(-1)?.recorded ?? current.recorded,
  };
}

/* ---- RACI (BL4) ---- */

export interface Raci {
  responsible: string;
  accountable: { role: TemplateRole; person: UserRef | null } | null;
  consulted: TemplateRole[];
}

/* the accountable role for a group value (or the owner without grouping) */
export function accountableRole(template: TemplateBody, groupValue: string | null): TemplateRole | null {
  /* with a grouping, a row whose group is not recorded has no accountable
     role — never the owner by default (Copilot review of #161) */
  const key = template.grouping ? (groupValue === null ? undefined : template.grouping.accountable[groupValue]) : template.ownerRole;
  return template.roles.find((r) => r.key === key) ?? null;
}

export function raciFor(
  template: TemplateBody,
  content: BaselineContent,
  groupValue: string | null,
  productName: string,
): Raci {
  const role = accountableRole(template, groupValue);
  const inStages = new Set(template.stages.flatMap((s) => s.roleKeys));
  return {
    responsible: productName,
    accountable: role ? { role, person: content.roleHolders[role.key] ?? null } : null,
    consulted: template.roles.filter((r) => inStages.has(r.key) && r.key !== role?.key),
  };
}

export const groupValueOf = (template: TemplateBody, row: Row): string | null => {
  if (!template.grouping) return null;
  const v = row.cells[template.grouping.columnKey]?.value;
  return typeof v === "string" ? v : null;
};

/* ---- acting (BL10, BL11, BL13) ---- */

export interface ActionInput {
  action: EventAction;
  roleKey: string;
  comment: string | null;
  basedOn: number;
}

export type Refusal = { status: 400 | 403 | 409; error: string };

/* The roles a version needs named before it can be submitted: the owner and
   every role in a stage. */
export function unnamedRoles(template: TemplateBody, content: BaselineContent): TemplateRole[] {
  const needed = new Set([template.ownerRole, ...template.stages.flatMap((s) => s.roleKeys)]);
  return template.roles.filter((r) => needed.has(r.key) && !content.roleHolders[r.key]);
}

/* What the caller may do right now on the current version, as role keys.
   The client offers only these; the server re-checks with actionReason. */
export function actionsFor(
  template: TemplateBody,
  version: VersionView,
  isCaller: (userId: string) => boolean,
): { submit: boolean; approveAs: string[] } {
  const holds = (key: string): boolean => {
    const p = version.content.roleHolders[key];
    return p !== null && p !== undefined && isCaller(p.userId);
  };
  const { status } = version;
  if (status.superseded) return { submit: false, approveAs: [] };
  return {
    submit: status.state === "draft" && holds(template.ownerRole),
    approveAs:
      status.state === "in-approval" && status.stage
        ? status.stage.roleKeys.filter((k) => holds(k) && !status.approved.includes(k))
        : [],
  };
}

export function actionReason(
  detail: BaselineDetail,
  versionNumber: number,
  body: unknown,
  isCaller: (userId: string) => boolean,
): Refusal | null {
  const b = (body ?? {}) as Partial<Record<keyof ActionInput, unknown>>;
  if (!EVENT_ACTIONS.includes(b.action as EventAction)) return { status: 400, error: "The action must be submit, approve or reject" };
  if (typeof b.roleKey !== "string") return { status: 400, error: "Name the role you are acting as" };
  if (typeof b.basedOn !== "number") return { status: 400, error: "Name how many actions you saw (basedOn)" };
  if (b.comment !== null && b.comment !== undefined && (typeof b.comment !== "string" || b.comment.length > BASELINE_LIMITS.comment))
    return { status: 400, error: `The comment must be text, ${BASELINE_LIMITS.comment} characters or fewer` };
  const current = detail.versions.at(-1)!;
  if (versionNumber !== current.version)
    return { status: 409, error: `Version ${current.version} is the current version; only it can be acted on` };
  if (b.basedOn !== current.events.length)
    return { status: 409, error: "Someone else acted on this version first; reload it and try again" };
  const template = templateAt(detail.templates, detail.baseline.templateId, current.content.templateRevision);
  if (!template) return { status: 409, error: "This version's template revision is missing" };
  const role = template.roles.find((r) => r.key === b.roleKey);
  if (!role) return { status: 400, error: `"${b.roleKey}" is not a role on this baseline` };
  const holder = current.content.roleHolders[role.key] ?? null;
  const { status } = current;

  if (b.action === "submit") {
    if (status.state !== "draft") return { status: 409, error: `Version ${current.version} is already ${APPROVAL_STATE_LABEL[status.state].toLowerCase()}` };
    if (role.key !== template.ownerRole) return { status: 403, error: `Only the ${ownerLabel(template)} submits for approval` };
    const missing = unnamedRoles(template, current.content);
    if (missing.length > 0) return { status: 400, error: `Name who holds ${missing.map((r) => r.label).join(", ")} before submitting` };
  } else {
    if (status.state !== "in-approval" || !status.stage)
      return { status: 409, error: `Version ${current.version} is not in approval` };
    if (!status.stage.roleKeys.includes(role.key))
      return { status: 403, error: `${role.label} does not approve in the ${status.stage.name} stage` };
    if (status.approved.includes(role.key)) return { status: 409, error: `${role.label} has already approved this stage` };
    if (b.action === "reject" && (typeof b.comment !== "string" || b.comment.trim() === ""))
      return { status: 400, error: "Give a reason for rejecting" };
  }
  if (holder === null) return { status: 403, error: `Nobody is named as ${role.label}` };
  if (!isCaller(holder.userId)) return { status: 403, error: `Only ${holder.name} can act as ${role.label}` };
  return null;
}

const ownerLabel = (t: TemplateBody): string => (t.roles.find((r) => r.key === t.ownerRole)?.label ?? t.ownerRole).toLowerCase();

/* ---- input rules ---- */

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown, max: number): v is string => typeof v === "string" && v.trim() !== "" && v.length <= max;
export const KEY = /^[a-z0-9][a-z0-9-]*$/;
const keyOk = (v: unknown): v is string => typeof v === "string" && KEY.test(v) && v.length <= BASELINE_LIMITS.key;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function uniqueKeys(items: { key: string }[], what: string): string | null {
  const seen = new Set<string>();
  for (const i of items) {
    if (seen.has(i.key)) return `Two ${what} share the key "${i.key}"`;
    seen.add(i.key);
  }
  return null;
}

export function templateBodyReason(v: unknown): string | null {
  if (!isObj(v)) return "The template is missing";
  if (!str(v.name, BASELINE_LIMITS.name)) return `Give the template a name, ${BASELINE_LIMITS.name} characters or fewer`;
  if (typeof v.description !== "string" || v.description.length > BASELINE_LIMITS.description)
    return `The description must be text, ${BASELINE_LIMITS.description} characters or fewer`;

  if (!Array.isArray(v.roles) || v.roles.length === 0) return "Add at least one role";
  if (v.roles.length > BASELINE_LIMITS.roles) return `At most ${BASELINE_LIMITS.roles} roles`;
  for (const [i, r] of v.roles.entries())
    if (!isObj(r) || !keyOk(r.key) || !str(r.label, BASELINE_LIMITS.label)) return `Role ${i + 1} needs a label`;
  const roles = v.roles as TemplateRole[];
  const dupRole = uniqueKeys(roles, "roles");
  if (dupRole) return dupRole;
  const roleKeys = roles.map((r) => r.key);
  if (typeof v.ownerRole !== "string" || !roleKeys.includes(v.ownerRole)) return "Pick which role owns the baseline";

  if (!Array.isArray(v.columns) || v.columns.length === 0) return "Add at least one column";
  if (v.columns.length > BASELINE_LIMITS.columns) return `At most ${BASELINE_LIMITS.columns} columns`;
  for (const [i, c] of v.columns.entries()) {
    const at = `Column ${i + 1}`;
    if (!isObj(c) || !keyOk(c.key) || !str(c.label, BASELINE_LIMITS.label)) return `${at} needs a label`;
    if (!COLUMN_TYPES.includes(c.type as ColumnType)) return `${at} (${c.label as string}): pick a type`;
    if (typeof c.required !== "boolean" || typeof c.estimateAllowed !== "boolean")
      return `${at} (${c.label as string}): say whether it is required and whether it may be an estimate`;
    if (!Array.isArray(c.options)) return `${at} (${c.label as string}): the options are missing`;
    if (c.type === "choice") {
      if (c.options.length === 0) return `${at} (${c.label as string}): a pick-list needs options`;
      if (c.options.length > BASELINE_LIMITS.options) return `${at} (${c.label as string}): at most ${BASELINE_LIMITS.options} options`;
      if (!c.options.every((o) => str(o, BASELINE_LIMITS.label))) return `${at} (${c.label as string}): every option needs text`;
      /* judged as stored: cleanTemplateBody trims */
      if (new Set(c.options.map((o) => (o as string).trim())).size !== c.options.length) return `${at} (${c.label as string}): an option is listed twice`;
    } else if (c.options.length > 0) return `${at} (${c.label as string}): only a pick-list has options`;
  }
  const columns = v.columns as Column[];
  const dupCol = uniqueKeys(columns, "columns");
  if (dupCol) return dupCol;

  if (v.grouping !== null) {
    const g = v.grouping;
    if (!isObj(g)) return "The grouping is malformed";
    const col = columns.find((c) => c.key === g.columnKey);
    if (!col || col.type !== "choice") return "Group rows by a pick-list column";
    const acc = g.accountable;
    if (!isObj(acc)) return `Pick the accountable role for every option of ${col.label}`;
    /* keys are matched trimmed, as cleanTemplateBody stores them */
    const byOption = new Map(Object.entries(acc).map(([k, r]) => [k.trim(), r]));
    const options = col.options.map((o) => o.trim());
    if (!options.every((o) => typeof byOption.get(o) === "string" && roleKeys.includes(byOption.get(o) as string)))
      return `Pick the accountable role for every option of ${col.label}`;
    if (byOption.size !== Object.keys(acc).length || [...byOption.keys()].some((k) => !options.includes(k)))
      return `The grouping names an option ${col.label} does not have`;
  }

  if (!Array.isArray(v.stages) || v.stages.length === 0) return "Add at least one approval stage";
  if (v.stages.length > BASELINE_LIMITS.stages) return `At most ${BASELINE_LIMITS.stages} approval stages`;
  for (const [i, s] of v.stages.entries()) {
    const at = `Stage ${i + 1}`;
    if (!isObj(s) || !keyOk(s.key) || !str(s.name, BASELINE_LIMITS.label)) return `${at} needs a name`;
    if (!Array.isArray(s.roleKeys) || s.roleKeys.length === 0) return `${at} (${s.name as string}): pick who approves`;
    if (!s.roleKeys.every((k) => roleKeys.includes(k as string))) return `${at} (${s.name as string}): approves with a role the template does not have`;
    if (new Set(s.roleKeys).size !== s.roleKeys.length) return `${at} (${s.name as string}): a role is listed twice`;
    if (!STAGE_RULES.includes(s.rule as StageRule)) return `${at} (${s.name as string}): pick everyone or any one`;
  }
  return uniqueKeys(v.stages as Stage[], "stages");
}

function personReason(v: unknown, at: string): string | null {
  if (!isObj(v) || !str(v.userId, BASELINE_LIMITS.text) || !str(v.name, BASELINE_LIMITS.text))
    return `${at}: pick the person from the org chart`;
  /* the picker keys a person with no Entra id on their name (PersonPicker's
     fallback); such a person could never act (BL7) */
  if ((v.userId as string).startsWith("name:")) return `${at}: ${v.name as string} has no Entra account in the org chart`;
  return null;
}

function cellReason(c: Column, cell: unknown, at: string): string | null {
  if (cell === undefined) return c.required ? `${at}: ${c.label} is required` : null;
  if (!isObj(cell) || typeof cell.unconfirmed !== "boolean") return `${at}: ${c.label} is malformed`;
  if (cell.unconfirmed && !c.estimateAllowed) return `${at}: ${c.label} cannot be marked unconfirmed`;
  const v = cell.value;
  /* judged as stored: cleanContent trims, so blank text is no value */
  if (v === null || (typeof v === "string" && v.trim() === "")) return c.required ? `${at}: ${c.label} is required` : null;
  switch (c.type) {
    case "text":
    case "longtext": {
      const max = c.type === "text" ? BASELINE_LIMITS.text : BASELINE_LIMITS.longtext;
      if (typeof v !== "string" || v.length > max) return `${at}: ${c.label} must be text, ${max} characters or fewer`;
      if (c.type === "text" && namesFinancialTerm(v)) return `${at}: ${c.label}: ${FINANCIAL_REFUSAL}`;
      return null;
    }
    case "number":
      return typeof v === "number" && Number.isFinite(v) ? null : `${at}: ${c.label} must be a number`;
    case "date":
      return typeof v === "string" && DATE.test(v) && !Number.isNaN(Date.parse(v)) ? null : `${at}: ${c.label} must be a date`;
    case "choice":
      return typeof v === "string" && c.options.includes(v) ? null : `${at}: ${c.label} must be one of ${c.options.join(", ")}`;
    case "person":
      return personReason(v, `${at}: ${c.label}`);
  }
}

export function rowsReason(v: unknown, template: TemplateBody): string | null {
  if (!Array.isArray(v)) return "The rows are missing";
  if (v.length > BASELINE_LIMITS.rows) return `At most ${BASELINE_LIMITS.rows} rows`;
  const ids = new Set<string>();
  const keys = template.columns.map((c) => c.key);
  for (const [i, row] of v.entries()) {
    const at = `Row ${i + 1}`;
    if (!isObj(row) || !str(row.id, BASELINE_LIMITS.key)) return `${at} needs an id`;
    /* judged as stored: cleanContent trims */
    const id = row.id.trim();
    if (ids.has(id)) return `${at}: the id "${id}" is used twice`;
    ids.add(id);
    if (!isObj(row.cells)) return `${at}: the cells are missing`;
    const extra = Object.keys(row.cells).find((k) => !keys.includes(k));
    if (extra) return `${at}: "${extra}" is not a column of this template`;
    for (const c of template.columns) {
      const reason = cellReason(c, row.cells[c.key], at);
      if (reason) return reason;
    }
    if (row.note !== null && !(typeof row.note === "string" && row.note.length <= BASELINE_LIMITS.note))
      return `${at}: the note must be text, ${BASELINE_LIMITS.note} characters or fewer`;
  }
  return null;
}

export function contentReason(v: unknown, template: TemplateRevision): string | null {
  if (!isObj(v)) return "The baseline's content is missing";
  if (v.templateRevision !== template.revision) return "The content names a different template revision";
  const holders = v.roleHolders;
  if (!isObj(holders)) return "Say who holds each role";
  const keys = template.roles.map((r) => r.key);
  const extra = Object.keys(holders).find((k) => !keys.includes(k));
  if (extra) return `"${extra}" is not a role of this template`;
  for (const r of template.roles) {
    const p = holders[r.key];
    if (p === undefined) return `Say who holds ${r.label}, or leave it not recorded`;
    if (p !== null) {
      const reason = personReason(p, r.label);
      if (reason) return reason;
    }
  }
  const people = Object.values(holders)
    .filter((p): p is UserRef => p !== null)
    .map((p) => p.userId);
  if (new Set(people).size !== people.length) return "One person holds two roles; each role needs its own person";
  if (typeof v.summary !== "string" || v.summary.length > BASELINE_LIMITS.summary)
    return `The summary must be text, ${BASELINE_LIMITS.summary} characters or fewer`;
  if (v.link !== null) {
    if (typeof v.link !== "string" || v.link.length > BASELINE_LIMITS.url || !URL.canParse(v.link) || !["https:", "http:"].includes(new URL(v.link).protocol))
      return "The link must be a web address";
  }
  return rowsReason(v.rows, template);
}

export function changeNoteReason(v: unknown): string | null {
  return str(v, BASELINE_LIMITS.changeNote) ? null : `Say what changed, ${BASELINE_LIMITS.changeNote} characters or fewer`;
}

export function newBaselineReason(v: unknown, productIds: readonly string[], template: TemplateRevision | null): string | null {
  if (!isObj(v)) return "The baseline is missing";
  if (typeof v.productId !== "string" || !productIds.includes(v.productId)) return "Pick the product from the product list";
  if (template === null || v.templateId !== template.templateId) return "Pick a template";
  if (!str(v.title, BASELINE_LIMITS.title)) return `Give it a title, ${BASELINE_LIMITS.title} characters or fewer`;
  return contentReason(v.content, template) ?? changeNoteReason(v.changeNote);
}

/* Keep only the known fields, trimmed — run after a reason came back null. */
export function cleanContent(v: BaselineContent, template: TemplateBody): BaselineContent {
  const cleanCell = (c: Column, cell: Cell | undefined): Cell | null => {
    if (!cell || cell.value === null || (typeof cell.value === "string" && cell.value.trim() === "")) return null;
    const value =
      typeof cell.value === "string"
        ? cell.value.trim()
        : typeof cell.value === "number"
          ? cell.value
          : { userId: cell.value.userId, name: cell.value.name.trim() };
    return { value, unconfirmed: c.estimateAllowed && cell.unconfirmed };
  };
  return {
    templateRevision: v.templateRevision,
    roleHolders: Object.fromEntries(
      template.roles.map((r) => {
        const p = v.roleHolders[r.key] ?? null;
        return [r.key, p === null ? null : { userId: p.userId, name: p.name.trim() }];
      }),
    ),
    summary: v.summary.trim(),
    link: v.link === null ? null : v.link.trim(),
    rows: v.rows.map((row) => ({
      id: row.id.trim(),
      cells: Object.fromEntries(
        template.columns.flatMap((c) => {
          const cell = cleanCell(c, row.cells[c.key]);
          return cell ? [[c.key, cell]] : [];
        }),
      ),
      note: row.note === null || row.note.trim() === "" ? null : row.note.trim(),
    })),
  };
}

export function cleanTemplateBody(v: TemplateBody): TemplateBody {
  return {
    name: v.name.trim(),
    description: v.description.trim(),
    roles: v.roles.map((r) => ({ key: r.key, label: r.label.trim() })),
    ownerRole: v.ownerRole,
    columns: v.columns.map((c) => ({
      key: c.key,
      label: c.label.trim(),
      type: c.type,
      required: c.required,
      estimateAllowed: c.estimateAllowed,
      options: c.type === "choice" ? c.options.map((o) => o.trim()) : [],
    })),
    grouping:
      v.grouping === null
        ? null
        : { columnKey: v.grouping.columnKey, accountable: Object.fromEntries(Object.entries(v.grouping.accountable).map(([o, r]) => [o.trim(), r])) },
    stages: v.stages.map((s) => ({ key: s.key, name: s.name.trim(), roleKeys: [...s.roleKeys], rule: s.rule })),
  };
}

/* "Robot chief" → "robot-chief", unique among `taken` */
export function keyFrom(label: string, taken: readonly string[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 30) || "item";
  let key = base;
  for (let n = 2; taken.includes(key); n++) key = `${base}-${n}`;
  return key;
}

/* the text shown for a cell */
export function cellText(cell: Cell | undefined): string {
  if (!cell || cell.value === null) return "";
  if (typeof cell.value === "object") return cell.value.name;
  return String(cell.value);
}

/* The template editor gives each item it adds a temporary key, because a
   label is typed after the item exists and stages and the grouping refer to
   items by key. On save, each item whose key is in `fresh` gets a key made
   from its label, and every reference to it follows (BL1). Existing keys
   never change: rows and events of drafted baselines are keyed by them. */
export function finalizeKeys(body: TemplateBody, fresh: ReadonlySet<string>): TemplateBody {
  const rekey = <T extends { key: string }>(items: T[], labelOf: (t: T) => string): Map<string, string> => {
    const taken = items.filter((i) => !fresh.has(i.key)).map((i) => i.key);
    const map = new Map<string, string>();
    for (const i of items)
      if (fresh.has(i.key)) {
        const key = keyFrom(labelOf(i), taken);
        taken.push(key);
        map.set(i.key, key);
      }
    return map;
  };
  const roles = rekey(body.roles, (r) => r.label);
  const columns = rekey(body.columns, (c) => c.label);
  const stages = rekey(body.stages, (s) => s.name);
  const role = (k: string): string => roles.get(k) ?? k;
  const column = (k: string): string => columns.get(k) ?? k;
  return {
    ...body,
    roles: body.roles.map((r) => ({ ...r, key: role(r.key) })),
    ownerRole: role(body.ownerRole),
    columns: body.columns.map((c) => ({ ...c, key: column(c.key) })),
    grouping:
      body.grouping === null
        ? null
        : {
            columnKey: column(body.grouping.columnKey),
            accountable: Object.fromEntries(Object.entries(body.grouping.accountable).map(([o, k]) => [o, role(k)])),
          },
    stages: body.stages.map((s) => ({ ...s, key: stages.get(s.key) ?? s.key, roleKeys: s.roleKeys.map(role) })),
  };
}

/* Move a version's content onto another revision of its template: role
   holders and cells the new revision no longer has are dropped, roles it
   adds start not recorded, and estimate flags it no longer allows are
   cleared (BL6). */
export function moveContent(content: BaselineContent, to: TemplateRevision): BaselineContent {
  const columns = new Map(to.columns.map((c) => [c.key, c]));
  return {
    ...content,
    templateRevision: to.revision,
    roleHolders: Object.fromEntries(to.roles.map((r) => [r.key, content.roleHolders[r.key] ?? null])),
    rows: content.rows.map((row) => ({
      ...row,
      /* a column that no longer allows estimates drops the flag, or the
         editor could neither show nor clear it (Copilot review of #161) */
      cells: Object.fromEntries(
        Object.entries(row.cells).flatMap(([k, cell]) => {
          const c = columns.get(k);
          return c ? [[k, { ...cell, unconfirmed: cell.unconfirmed && c.estimateAllowed }]] : [];
        }),
      ),
    })),
  };
}
